import { effect, EffectRef, inject, Injectable, provideAppInitializer } from '@angular/core';
import { UserContext } from '@app/modules/authentication/store/auth/user-context.model';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import type { RumPublicApi, RumInitConfiguration } from '@datadog/browser-rum-core';
import type { User as DatadogUser } from '@datadog/browser-core';
import Environment from '@env';

interface DatadogUserInfo extends DatadogUser {
  id: string;
  organization: string | undefined;
  organizationId: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class DatadogRumService {
  private datadogRum: RumPublicApi;
  private userContextService = inject(UserContextService);
  private config: RumInitConfiguration;
  private enabled: boolean;
  private handleUserContextChanges: EffectRef;

  constructor() {
    const { enabled = false, config = {} } = Environment?.datadog?.rum ?? {};
    this.enabled = typeof enabled !== 'boolean' ? false : enabled;
    this.config = {
      ...config,
      beforeSend: (event, context) => {
        if (event?.type === 'error') {
          if (
            event?.error?.message?.toLowerCase()?.includes('mapsgl') ||
            event?.error?.stack?.toLowerCase()?.includes('mapsgl') ||
            event?.error?.handling_stack?.toLowerCase()?.includes('mapsgl')
          ) {
            return false;
          }
        }

        return true;
      }
    };

    if (this.enabled) {
      this.handleUserContextChanges = effect(async () => {
        const userContext = this.userContextService.getContext();

        if (!userContext) {
          this.datadogRum?.stopSession();
          return;
        }

        const userInfo = this.getUserInfoFromContext(userContext);

        if (!this.datadogRum) {
          await this.startSession(userInfo);
        } else {
          this.updateUserDetails(userInfo);
        }
      });
    }
  }

  private async startSession(userInfo: DatadogUserInfo): Promise<void> {
    if (!!this.datadogRum || !userInfo) {
      return;
    }

    try {
      const { datadogRum } = await import('@datadog/browser-rum');
      this.datadogRum = datadogRum;
      this.datadogRum?.init(this.config);

      this.datadogRum?.onReady(() => {
        this.datadogRum?.setUser(userInfo);
      });
    } catch (err) {
      console.error(err);
      this.handleUserContextChanges.destroy();
      this.datadogRum = null;
    }
  }

  private updateUserDetails(userInfo: DatadogUserInfo): void {
    if (!this.datadogRum || !userInfo) {
      return;
    }

    const ddUser = this.datadogRum?.getUser() ?? {};

    Object.entries(userInfo).forEach(([key, value]) => {
      if (ddUser[key] !== value) {
        this.datadogRum?.setUserProperty(key, value);
      }
    });
  }

  private getUserInfoFromContext(userContext: UserContext): DatadogUserInfo {
    if (!userContext) {
      return null;
    }

    const { userProfileUId, organizationUId, organizationDisplayName } = userContext;

    return {
      id: userProfileUId,
      organizationId: organizationUId,
      organization: organizationDisplayName
    };
  }
}

export const provideDatadogRUM = provideAppInitializer(() => {
  inject(DatadogRumService);
});
